import {combineLatest, merge} from 'rxjs';
import {map, scan} from 'rxjs/operators';
import {numUtil, plus} from '@/helper/num';
import {currenciesCache$} from '@/config';
import {wsLogout$} from '@/user/auth-ws';
import {launchPoolMsg$} from '@/raw/launch-pool';
import type {ReduceAction} from '@/types';
import LaunchPoolMsg = lp.LaunchPoolMsg;
import LaunchPoolRaw = lp.LaunchPoolRaw;

type Cache = Map<string, LaunchPoolRaw>;
type Action = ReduceAction<Cache>;

const launchPoolsData$ = launchPoolMsg$.pipe(map((data: LaunchPoolMsg) => data.payload));

const action$ = combineLatest([launchPoolsData$, currenciesCache$]).pipe(
  map(([launchPoolsData, currenciesCache]): Action => {
    const items = launchPoolsData.map((launchPool) => {
      const item = currenciesCache.get(launchPool.investCurrency);
      if (!item) {
        return launchPool;
      }
      const {name, valueScale, valuePrecision} = item;
      const totalInvestAmountEv = plus(
        launchPool.investAmountEv,
        numUtil.floor(launchPool.stakingBalanceRv || 0, 0, -valueScale)
      );
      return {
        ...launchPool,
        name,
        investAmount: numUtil.floor(launchPool.investAmountEv, valuePrecision, valueScale),
        stakingBalanceRv: launchPool.stakingBalanceRv || '0',
        stakingBalance: numUtil.floor(launchPool.stakingBalanceRv || 0, valuePrecision),
        totalInvestAmountEv: Number(totalInvestAmountEv),
        totalInvestAmount: numUtil.floor(totalInvestAmountEv, valuePrecision, valueScale),
      };
    });
    return function (cache: Cache) {
      items.forEach((launchPool: LaunchPoolRaw) => {
        cache.set(launchPool.poolId, launchPool);
      });
      return cache;
    };
  })
);

const reset$ = wsLogout$.pipe(
  map(
    (): ReduceAction<Cache> => (cache: Cache) => {
      cache.clear();
      return cache;
    }
  )
);

export const launchPoolCache$ = merge<Action[]>(action$, reset$).pipe(
  scan((p: Cache, c: Action) => c(p), new Map<string, LaunchPoolRaw>())
);
